import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import posthog from 'posthog-js';
import { FileText, Upload, AlertTriangle, Check } from 'lucide-react';
import API_URL from '../../config';
import { getDocumentTypeSelectGroups, DOCUMENT_TYPES_FOR_UPLOAD } from '../../config/documentTypes';
import '../../App.css';

function isKnownDocumentType(value) {
  return DOCUMENT_TYPES_FOR_UPLOAD.some((t) => t.value === value);
}

const DocumentUpload = ({
  onUploadSuccess,
  vehicles = [],
  onClose,
  initialTaggedEntityType = '',
  initialTaggedEntityId = '',
  initialDocumentType = '',
}) => {
  const defaultDocType = useMemo(() => {
    if (initialDocumentType && isKnownDocumentType(initialDocumentType)) {
      return initialDocumentType;
    }
    return 'medical_card';
  }, [initialDocumentType]);

  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState(defaultDocType);
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const [taggedEntityType, setTaggedEntityType] = useState(
    () => (initialTaggedEntityType || '').toLowerCase() || 'business'
  );
  const [taggedEntityId, setTaggedEntityId] = useState(() => initialTaggedEntityId || '');

  useEffect(() => {
    const t = (initialTaggedEntityType || '').toLowerCase();
    setTaggedEntityType(t && (t === 'vehicle' || t === 'driver') ? t : 'business');
    setTaggedEntityId(initialTaggedEntityId || '');
  }, [initialTaggedEntityType, initialTaggedEntityId]);

  useEffect(() => {
    if (initialDocumentType && isKnownDocumentType(initialDocumentType)) {
      setDocumentType(initialDocumentType);
    }
  }, [initialDocumentType]);

  const typeSelectGroups = getDocumentTypeSelectGroups();

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (fileToValidate) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(fileToValidate.type)) {
      setError('Invalid file type. Please upload PDF, JPG, or PNG files only.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (fileToValidate.size > maxSize) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setFile(fileToValidate);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    setError('');
    setSuccess(false);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      if (expirationDate) {
        formData.append('expirationDate', expirationDate);
      }
      if (notes) {
        formData.append('notes', notes);
      }
      if (vehicleId) {
        formData.append('vehicleId', vehicleId);
      }

      const te = (taggedEntityType || 'business').toLowerCase();
      if (te === 'vehicle' || te === 'driver') {
        formData.append('taggedEntityType', te);
        if (taggedEntityId) {
          formData.append('taggedEntityId', taggedEntityId);
        }
      }

      await axios.post(
        `${API_URL}/api/documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      posthog.capture('document_uploaded', {
        documentType: documentType,
      });

      setSuccess(true);
      setFile(null);
      setDocumentType(defaultDocType);
      setExpirationDate('');
      setNotes('');
      setVehicleId('');
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call success callback
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
          setSuccess(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="document-upload-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 className="upload-title">Upload New Document</h3>
        {onClose && (
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={onClose}>Cancel</button>
        )}
      </div>
      <form onSubmit={handleSubmit} className="upload-form">
        {taggedEntityType === 'driver' && taggedEntityId && (
          <p className="text-small" style={{ marginBottom: '12px', color: 'var(--muted)' }}>
            This upload will be saved to this driver&apos;s qualification file.
          </p>
        )}
        {taggedEntityType === 'vehicle' && taggedEntityId && (
          <p className="text-small" style={{ marginBottom: '12px', color: 'var(--muted)' }}>
            This upload will be linked to the selected vehicle.
          </p>
        )}
        {/* File Drop Zone */}
        <div
          className={`file-drop-zone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {file ? (
            <div className="file-selected">
              <FileText size={24} className="file-icon" />
              <span className="file-name">{file.name}</span>
              <span className="file-size">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          ) : (
            <div className="file-drop-content">
              <Upload size={24} className="drop-icon" />
              <p className="drop-text">Drag & drop file here or click to browse</p>
              <p className="drop-hint">PDF, JPG, PNG (max 10MB)</p>
            </div>
          )}
        </div>

        {/* Document Type */}
        <div className="form-group">
          <label className="form-label">Document Type *</label>
          <select
            className="form-select document-type-selector"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            required
          >
            {typeSelectGroups.map(({ group, options }) => (
              <optgroup key={group} label={group}>
                {options.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                    {type.required ? ' (required)' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Expiration Date */}
        <div className="form-group">
          <label className="form-label">Expiration Date (Optional)</label>
          <input
            type="date"
            className="form-input"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
          />
        </div>

        {/* Vehicle Selector */}
        {vehicles.length > 0 && (
          <div className="form-group">
            <label className="form-label">Link to Vehicle (Optional)</label>
            <select
              className="form-select"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
            >
              <option value="">None</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Notes */}
        <div className="form-group">
          <label className="form-label">Notes (Optional)</label>
          <textarea
            className="form-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes about this document..."
            rows={3}
          />
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span className="progress-text">{uploadProgress}%</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="upload-error">
            <AlertTriangle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="upload-success">
            <Check size={20} />
            <span>Document uploaded successfully!</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="btn upload-submit-btn"
          disabled={isUploading || !file}
        >
          {isUploading ? 'Uploading...' : 'Upload Document'}
        </button>
      </form>
    </div>
  );
};

export default DocumentUpload;



